// src/pages/Industries.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Table,
    Dropdown,
    Menu,
    Button,
    Row,
    Col,
    Modal,
    Form,
    Input,
} from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import theme from "../theme";
import { useUser } from "../contexts/UserContext";
import {
    listIndustries,
    createIndustry,
    deleteIndustry,
} from "../services/industries";

/* ---------- types ---------- */
interface IndustryRow {
    id: string;
    name: string;
    colorHex: string;
    tests: number;
    applicants: number;
}

const IndustriesList: React.FC = () => {
    const navigate = useNavigate();
    const { isSuperAdmin } = useUser();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toDelete, setToDelete] = useState<IndustryRow | null>(null);
    const [rows, setRows] = useState<IndustryRow[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [form] = Form.useForm();
    const inputRef = useRef<any>(null);

    // Filter rows based on search term
    const filteredRows = rows.filter(row =>
        row.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
    }, []);

    const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        handleSearchChange(e.target.value);
    }, [handleSearchChange]);

    const clearSearch = useCallback(() => {
        handleSearchChange("");
    }, [handleSearchChange]);

    const handleRowClick = useCallback((record: IndustryRow) => {
        navigate(`/view-industry/${record.id}`);
    }, [navigate]);

    const onRow = useCallback((record: IndustryRow) => ({
        onClick: () => handleRowClick(record),
        style: { cursor: 'pointer' }
    }), [handleRowClick]);

    /* ---------- fetch industries once ---------- */
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const data = await listIndustries(); // GET /api/industries/
                const mapped = data.map((i: any) => ({
                    id: String(i.id),
                    name: i.name,
                    colorHex: i.color_hex,
                    tests: i.tests_count ?? 0,
                    applicants: i.candidates_count ?? 0,
                }));
                setRows(mapped);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const confirmDelete = async () => {
        if (!toDelete) return;
        try {
            setDeleting(true);
            await deleteIndustry(Number(toDelete.id)); // API call
            setRows((prev) => prev.filter((r) => r.id !== toDelete.id));
        } catch (err) {
            console.error(err);
        } finally {
            setDeleting(false);
            setToDelete(null);
        }
    };

    /* ---------- table columns ---------- */
    const columns = React.useMemo(() => [
        { title: "Industry", dataIndex: "name", key: "name" },
        { title: "Assessments", dataIndex: "tests", key: "tests" },
        { title: "Applicants", dataIndex: "applicants", key: "applicants" },
        {
            key: "actions",
            width: 60,
            render: (_: unknown, row: IndustryRow) => {
                const menu = (
                    <Menu>
                        <Menu.Item onClick={() => navigate(`/assessments/${row.id}`)}>
                            View Assessments
                        </Menu.Item>
                        {isSuperAdmin() && (
                            <Menu.Item onClick={() => navigate(`/industries/${row.id}/functions`)}>
                                View Functions
                            </Menu.Item>
                        )}
                        {isSuperAdmin() && (
                            <Menu.Item onClick={() => navigate(`/industries/${row.id}/roles`)}>
                                View Roles
                            </Menu.Item>
                        )}
                        {isSuperAdmin() && (
                            <Menu.Item onClick={() => navigate(`/industries/${row.id}/software`)}>
                                View Software
                            </Menu.Item>
                        )}
                        {isSuperAdmin() && (
                            <Menu.Item danger onClick={() => setToDelete(row)}>
                                Remove
                            </Menu.Item>
                        )}
                    </Menu>
                );
                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown overlay={menu} trigger={["click"]}>
                            <Button
                                type="text"
                                icon={<EllipsisOutlined style={ellipsisIconStyle} />}
                            />
                        </Dropdown>
                    </div>
                );
            },
        },
    ], [navigate, isSuperAdmin, setToDelete]);

    const emptyText = React.useMemo(() => (
        <div style={emptyStateContainer}>
            {searchTerm ? (
                // Search returned no results
                <>
                    <div style={emptyStateText}>No industries found for "{searchTerm}"</div>
                    <div style={emptyStateSubtext}>Try adjusting your search terms</div>
                </>
            ) : (
                // No industries exist at all
                <>
                    <div style={emptyStateText}>No industries found</div>
                    <div style={emptyStateSubtext}>Create your first industry to get started</div>
                </>
            )}
        </div>
    ), [searchTerm]);

    /* ---------- render ---------- */
    return (
        <>
            <div style={headerStyle}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <h1 style={headingStyle}>Industries</h1>
                    </Col>
                    <Col>
                        <Row gutter={16} align="middle">
                            <Col>
                                <Input
                                    ref={inputRef}
                                    key="industries-search"
                                    placeholder="Search/filter"
                                    value={searchTerm}
                                    onChange={handleSearchInputChange}
                                    style={searchInputStyle}
                                    suffix={
                                        <span
                                            style={{
                                                ...clearButtonStyle,
                                                opacity: searchTerm ? 1 : 0,
                                                pointerEvents: searchTerm ? 'auto' : 'none'
                                            }}
                                            onClick={clearSearch}
                                        >
                                            ×
                                        </span>
                                    }
                                />
                            </Col>
                            <Col>
                                {isSuperAdmin() && (
                                    <Button
                                        onClick={() => setOpen(true)}
                                        style={addButtonStyle}
                                    >
                                        Add new industry
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>
            <div className="section-content layout">
                <div className="card">
                    <div className="card-body sectionMinHeight">
                        <Table
                            className="table"
                            columns={columns}
                            dataSource={filteredRows}
                            rowKey="id"
                            pagination={false}
                            bordered
                            loading={loading}
                            onRow={onRow}
                            locale={{
                                emptyText: emptyText
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* ---- create-industry modal ---- */}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                centered
                width={430}
				style={{ borderRadius: 18 }}
                bodyStyle={{ padding: "32px 40px" }}
            >
                <h2 style={modalTitle}>Add new industry</h2>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={async (values) => {
                        try {
                            setSaving(true);

                            const created = await createIndustry({
                                name: values.name,
                                color_hex: "#4285f4", // Default color
                            });

                            // Optimistically append to table
                            setRows((prev) => [
                                ...prev,
                                {
                                    id: String(created.id),
                                    name: created.name,
                                    colorHex: created.color_hex || "#0050FF",
                                    tests: 0,
                                    applicants: 0,
                                },
                            ]);

                            form.resetFields();
                            setOpen(false);
                        } catch (err: any) {
                            console.error(err);
                        } finally {
                            setSaving(false);
                        }
                    }}
                >
                    <Form.Item
						className="input-form"
                        name="name"
                        rules={[{ required: true, message: "Please enter a name" }]}
                    >
                        <Input 
							placeholder="Industry name" 
							style={input} 
							/>
                    </Form.Item>

                    <Row justify="space-between" gutter={16}>
                        <Col>
                            <Button
								style={cancelButtonStyling} 
								onClick={() => setOpen(false)}
							>Cancel</Button>
                        </Col>
                        <Col>
                            <Button
                                htmlType="submit"
                                loading={saving}
                                style={saveBtn}
                            >
                                Save
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            {/* ---- delete industry modal ---- */}
            <Modal
                open={!!toDelete}
                onCancel={() => setToDelete(null)}
                onOk={confirmDelete}
                okText="Delete"
                okButtonProps={{ danger: true, loading: deleting }}
                cancelButtonProps={{ disabled: deleting }}
                title="Delete industry?"
            >
                {`Are you sure you want to delete "${toDelete?.name}"? This action cannot be undone.`}
            </Modal>
        </>
    );
};

/* ---------- styles (unchanged) ---------- */
const bg: React.CSSProperties = {
    minHeight: "100vh",
    padding: "20px 6%",
};
const heading: React.CSSProperties = {
    color: theme.colors.primary,
    fontWeight: 700,
    fontSize: 28,
    margin: 0,
};
const card: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: 8,
    padding: 0,
};

const modalTitle: React.CSSProperties = {
    textAlign: "center",
    color: theme.colors.primary,
    fontWeight: 700,
    fontSize: 24,
    marginBottom: 24,
};
const saveBtn: React.CSSProperties = { 
	background: "#1D1F48",
	color: "white",
    borderRadius: 9999,
    padding: "0 24px",
	border: "solid 1px #1D1F48",
	height: 38,
	minWidth: 110
};

const headerStyle: React.CSSProperties = {
    padding: "20px 20px 0px 24px",
};

const headingStyle: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 700,
    color: theme.colors.primary,
    margin: 0,
};

const addButtonStyle: React.CSSProperties = {
	background: "#1D1F48",
	color: "white",
    borderRadius: 9999,
    border: "none",
    padding: "0 24px",
};

const cancelButtonStyling: React.CSSProperties = {
	color: "#1D1F48",
	background: "white",
    borderRadius: 9999,
    padding: "0 24px",
	border: "solid 1px #1D1F48",
	height: 38,
	minWidth: 110
}

const emptyStateContainer: React.CSSProperties = {
    padding: "40px",
    textAlign: "center",
};

const emptyStateText: React.CSSProperties = {
    fontSize: "16px",
    color: "#666",
    marginBottom: "8px",
    fontWeight: 500,
};

const emptyStateSubtext: React.CSSProperties = {
    fontSize: "14px",
    color: "#999",
};

const searchInputStyle: React.CSSProperties = {
    borderRadius: 24,
    width: 240,
    background: "#eaf2ff",
    border: "none",
    color: "#23244A",
    fontWeight: 500,
    height: 32,
    fontSize: 14,
    paddingLeft: 24,
    paddingRight: 24,
};

const clearButtonStyle: React.CSSProperties = {
    cursor: 'pointer',
    color: '#787A7D',
    marginRight: 4,
};

const ellipsisIconStyle: React.CSSProperties = {
    fontSize: 20,
};

const input: React.CSSProperties = {
    borderRadius: "9999px",
    border: "none",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    background: "#E5EBFA",
    paddingLeft: "5%",
    fontSize: 14,
    height: 38,
};

export default IndustriesList;
